/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React, { FC, Fragment, useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { compact, isEmpty } from "lodash"

import { useTranslationsMap } from "common/utils/i18n"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { ExtraReviewsFilterConfig, Review } from "types/api/menuFeedback.type"
import { ActionType } from "types/models/feedback"
import InfiniteScroll from "components/InfiniteScroll"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { useCanViewStoreList } from "order-manager/hooks/useCanViewStoreList"
import { isFeedbackDownloadReviewsEnabled } from "utils/feedback_helper"

import useSingleStoreUser from "hooks/useSingleStoreUser"

import { FeatureFlag } from "constants/features"
import { storageHelper } from "utils/storage"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { Tabs } from "@duxton-web/ui"
import { Redirect, useHistory, useParams } from "react-router"
import { RatingSummary } from "./FeedbackRatings/RatingSummary"
import { FeedbackFilters } from "./FeedbackFilters"
import FeedbackList from "./FeedbackList"
import { FeedbackDrawerContainer } from "./FeedbackDrawerContainer"
import {
  DEFAULT_PAGE_SIZE,
  FeedbackTabKeys,
  TAB_ROUTES,
  VALID_TAB_FRAGMENTS,
} from "./consts"
import FeedbackDownload from "./FeedbackDownload"
import {
  useDeprecateReviewOrderIdFlag,
  DeprecateReviewOrderIdFlagProvider,
} from "./useDeprecateReviewOrderIdFlag"
import FeedbackTopFiltersNew from "./FeedbackFilters/FeedbackTopFiltersNew"
import { IncidentList } from "./Incidents"

export const Feedback: React.FC = () => {
  const { feedback, menu } = useSelector((state: AppState) => state)
  const { outletV2, outlets } = menu
  const { loading, overview, overviews, filterState, reviews } = feedback
  const dispatch = useDispatch()

  const textMap = useTranslationsMap({
    customerRatings: "feedback.headings.customerRatings",
    customerReviews: "feedback.headings.customerReviews",
  })

  const onClickRow = (actionType: ActionType, review: Review) => {
    dispatch(
      actionCreator.feedbackDrawer.toggleReply({
        visible: true,
        actionType,
        review,
      }),
    )
  }

  const canViewStoreList = useCanViewStoreList()

  const { isSingleStoreUser, storeID } = useSingleStoreUser()

  // available stores
  let merchantIDs = outlets?.map(o => o.merchantID) ?? []
  // use fallback value from local storage
  if (isEmpty(merchantIDs) || !canViewStoreList)
    merchantIDs = compact([storeID])

  const singleMerchantID = isSingleStoreUser ? merchantIDs[0] : undefined

  const handleLoadMore = () => {
    if (!isEmpty(merchantIDs)) {
      dispatch(
        actionCreator.feedback.fetchMoreReviews({
          body: {
            ...filterState,
          },
        }),
      )
    }
  }

  const deprecateReviewOrderIdFlag = useDeprecateReviewOrderIdFlag()

  useEffect(() => {
    if (singleMerchantID) {
      // in single store mode, outlets is empty so we need to fetch the store detail here
      dispatch(
        actionCreator.menu.fetchMerchantDetail({
          merchantID: singleMerchantID,
          countryCode: storageHelper.getUserCountry() || "",
        }),
      )
    }
  }, [dispatch, singleMerchantID])

  const supportsDineIn =
    outletV2?.supportDineIn ||
    outlets?.some(info => {
      return info.deliverOption?.includes("DINEIN")
    })

  const hasSelfPickup =
    outletV2?.supportTakeaway ||
    outlets?.some(info => info.deliverOption?.includes("TAKEAWAY"))

  const enableNonTxnReview =
    storageHelper.isFeatureEnabled(FeatureFlag.enableNonTxnReviewMexPortal) &&
    (storageHelper.isFeatureEnabled(
      FeatureFlag.enableNonTxnReviewMexPortalForDeliveryOnly,
    ) ||
      supportsDineIn)

  useEffect(() => {
    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackHomePage,
      action: EventAction.pageView,
      labels: {
        DEPRECATE_REVIEW_ORDER_ID: deprecateReviewOrderIdFlag,
      },
    })
    // only want to handle this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFetchReviews = (
    extras?: ExtraReviewsFilterConfig & { merchantIDs?: string[] },
  ) => {
    if (!isEmpty(merchantIDs)) {
      const body = {
        ...filterState,
        ...extras,
        nextToken: undefined, // ensures a new list sequence
      }

      // to bypass filters
      if (isSingleStoreUser) body.merchantIDs = merchantIDs

      dispatch(
        actionCreator.feedback.fetchReviews({
          body,
        }),
      )
    }
  }

  useEffect(() => {
    if (
      (reviews?.length ?? 0) <= DEFAULT_PAGE_SIZE &&
      filterState?.nextToken &&
      !loading
    ) {
      handleLoadMore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviews, filterState, loading])

  return (
    <Fragment>
      <div
        css={css({
          marginBottom: "23px",
        })}
      >
        <FeedbackTopFiltersNew
          enableNonTxnReview={enableNonTxnReview}
          onFetchReviews={handleFetchReviews}
          isSingleStore={isSingleStoreUser}
        />
      </div>

      <div
        css={css({
          marginBottom: "25px",
        })}
      >
        <span
          css={css({
            fontWeight: 500,
            fontSize: "16px",
          })}
        >
          {textMap.customerRatings}
        </span>
        <div
          css={css({
            marginTop: "26px",
            display: "flex",
            gap: "16px",
          })}
        >
          {overview && (
            <React.Fragment>
              <RatingSummary
                feedbackOverview={overview}
                hasSelfPickup={hasSelfPickup}
              />
            </React.Fragment>
          )}
          {overviews &&
            Object.entries(overviews).map(([serviceType, feedbackOverview]) => (
              <React.Fragment key={serviceType}>
                <RatingSummary
                  key={serviceType}
                  feedbackOverview={feedbackOverview}
                  serviceType={serviceType}
                  hasSelfPickup={hasSelfPickup}
                />
              </React.Fragment>
            ))}
        </div>
      </div>

      <div
        css={css({
          marginBottom: "16px",
        })}
      >
        <span
          css={css({
            fontWeight: 600,
            fontSize: "14px",
          })}
        >
          {textMap.customerReviews}
        </span>
      </div>

      <div
        css={css({
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "14px",
        })}
      >
        <FeedbackFilters onFetchReviews={handleFetchReviews} />
        {isFeedbackDownloadReviewsEnabled() && <FeedbackDownload />}
      </div>

      <InfiniteScroll
        onReachEnd={handleLoadMore}
        hasMore={!isEmpty(filterState?.nextToken)}
        loading={loading}
      >
        <FeedbackList
          data-testid="feedback-list-table"
          onClick={onClickRow}
          onFetchReviews={handleFetchReviews}
          enableNonTxnReview={enableNonTxnReview}
        />
      </InfiniteScroll>
      <FeedbackDrawerContainer />
    </Fragment>
  )
}

export function FeedbackTabSwitcher() {
  const textMap = useTranslationsMapTyped({
    feedbackTitle: "feedback.tabTitle",
    incidentTitle: "incidents.title",
  })
  const params = useParams<{ fragment?: string }>()
  const history = useHistory()
  const { fragment } = params
  const enableIncidents = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.mexPortalEnableIncidents,
  )

  const handleTabChange = useCallback(
    (key: string) => {
      if (key === fragment) {
        return
      }
      history.push(TAB_ROUTES[key as FeedbackTabKeys] ?? TAB_ROUTES.feedback)
    },
    [fragment, history],
  )

  if (!enableIncidents) {
    if (fragment) {
      // remove fragment from the url since it's only used by incidents for now
      history.replace(TAB_ROUTES.feedback)
    }
    // do not render the tab switcher
    return <Feedback />
  }

  if (fragment && !VALID_TAB_FRAGMENTS.includes(fragment as FeedbackTabKeys)) {
    // note: if we ever implement Feedback detail deeplinking, do feel free to remove the redirect
    return <Redirect to={TAB_ROUTES.feedback} />
  }

  return (
    <Fragment>
      <Tabs activeKey={fragment} onChange={handleTabChange}>
        <Tabs.TabPane tab={textMap.feedbackTitle} key="feedback">
          <Feedback />
        </Tabs.TabPane>
        <Tabs.TabPane tab={textMap.incidentTitle} key="incidents">
          <IncidentList />
        </Tabs.TabPane>
      </Tabs>
    </Fragment>
  )
}

export const WithFeedbackTitle = () => {
  return (WrappedComponent: React.FC) => {
    const Component: FC = props => {
      const textMap = useTranslationsMap({
        title: "feedback.title",
      })

      return (
        <div
          css={css({
            padding: "35px",
            minWidth: "900px",
          })}
        >
          <div
            css={css({
              marginBottom: "33px",
            })}
          >
            <span
              css={css({
                fontWeight: 500,
                fontSize: "32px",
              })}
            >
              {textMap.title}
            </span>
          </div>
          <WrappedComponent {...props} />
        </div>
      )
    }
    return Component
  }
}

export const WitDeprecateReviewOrderIdFlagProvider = () => {
  return (WrappedComponent: React.FC): React.FC => {
    const Component: FC = props => {
      return (
        <DeprecateReviewOrderIdFlagProvider>
          <WrappedComponent {...props} />
        </DeprecateReviewOrderIdFlagProvider>
      )
    }
    return Component
  }
}

export const FeedbackWithHeader = WithFeedbackTitle()(
  WitDeprecateReviewOrderIdFlagProvider()(FeedbackTabSwitcher),
)
